import React, { useCallback, useEffect, useRef, useState } from "react";
import { LayoutDashboard, CalendarDays, Users, GraduationCap, Receipt, Settings, Bell, Lock, LogOut, Inbox, ChevronRight, ShieldCheck, Briefcase, Bot, ArrowLeft, Smartphone, CalendarClock, Ticket } from "lucide-react";
import { api, connexion, garderLaSession, googleActif, jeton, urlConnexionGoogle } from "./api";
import { Eyebrow } from "./ui";
import Dashboard from "./Dashboard";
import { FournisseurConfirmation } from "./Confirmation";
import Notifications from "./Notifications";
import Messages from "./Messages";
import Liste from "./Liste";
import Eleves from "./Eleves";
import Attestations from "./Attestations";
import Facturation from "./Facturation";
import Planning from "./Planning";
import CoursPrives from "./CoursPrives";
import Reglages from "./Reglages";
import Journal from "./Journal";
import MenuApplication from "./MenuApplication";
import Billetterie from "./Billetterie";
import SpectacleNoel from "./SpectacleNoel";
import EmailsEnvoyes from "./EmailsEnvoyes";
import ReservationsPrivees from "./ReservationsPrivees";
import { useChantiers } from "../lib/useChantiers";
import Assistant from "./Assistant";
import AideConsole from "./AideConsole";
import Profs from "./Profs";
import Envois from "./Envois";
import VisionneusePdf from "./VisionneusePdf";
import { CalendrierAnnee } from "../components/CalendrierAnnee";

/* Les menus de la console (choix de l'école, 07.10.2026). Quand un menu regroupe plusieurs
   écrans, ils se choisissent par des onglets en haut de page. Les identifiants des écrans
   ne changent pas : « Écrire à ce groupe », les alertes et les liens internes continuent
   d'ouvrir directement le bon écran.
   Le menu « Application » revient (école, 20.10.2026), complet : statistiques, qui a
   l'app, annonces et notifications, « Voir l'app » (simulateur) et historique. Les
   anciennes adresses mènent au bon onglet (`ANCIENS`). */
const TABS = [
  { id: "dashboard", label: "Tableau de bord", Icon: LayoutDashboard, ecrans: [["dashboard", "Tableau de bord"]] },
  { id: "notifications", label: "À traiter", Icon: Bell, ecrans: [["notifications", "À traiter"]] },
  { id: "planning", label: "Planning", Icon: CalendarDays,
    ecrans: [["planning", "Planning des cours"], ["calendrier", "Calendrier de l'année"]] },
  { id: "liste", label: "Liste", Icon: GraduationCap,
    // [écran, libellé, grand chantier qui le rend visible (test d'abord)]
    ecrans: [["liste", "Listes"]] },
  // Cartes 10 cours : dans Facturation, à côté des parrainages (Jen, 31.10.2026 : « je trouve
  // pas qu'il fait du sens dans Élèves ») ; l'ancienne adresse « cartes-10 » y mène.
  // « Cours d'essai » : une section du tableau de bord (Jen, 01.11.2026, d'abord sous Élèves) ;
  // l'ancienne adresse « essais » y mène.
  { id: "eleves", label: "Élèves", Icon: Users, ecrans: [["eleves", "Élèves"]] },
  // L'ordre de Jen (31.10.2026) : Facturation, Profs, Cours privés, Spectacles, Messages.
  { id: "facturation", label: "Facturation", Icon: Receipt, ecrans: [["facturation", "Facturation"]] },
  { id: "profs", label: "Profs", Icon: Briefcase, ecrans: [["profs", "Profs"]] },
  // Cours privés : un menu à lui (Jen, 30.10.2026) — le calendrier de tous les profs, les
  // réservations et les disponibilités de chacun.
  { id: "cours-prives", label: "Cours privés", Icon: CalendarClock, ecrans: [["cours-prives", "Cours privés"]] },
  // Spectacles de juin 2027 : un menu à lui (Jen, 31.10.2026 : « avec la billetterie et les
  // bénévoles, un onglet dédié ») — grand chantier, seulement là où il est ouvert.
  { id: "spectacles", label: "Spectacles", Icon: Ticket, chantier: "spectacles_juin",
    ecrans: [["spectacles", "Vue d'ensemble"], ["spectacles-benevoles", "Bénévoles"], ["spectacles-commandes", "Billets & commandes"],
      ["spectacles-entree", "Entrée (QR code)"], ["spectacles-reglages", "Réglages de la vente"], ["spectacles-noel", "Spectacle de Noël"]] },
  { id: "messages", label: "Messages", Icon: Inbox,
    ecrans: [["messages", "Messages reçus"], ["envois", "Envois groupés"], ["emails-envoyes", "E-mails envoyés"]] },
  { id: "attestations", label: "Attestations", Icon: ShieldCheck, ecrans: [["attestations", "Attestations"]] },
  { id: "assistant", label: "Assistant IA", Icon: Bot, ecrans: [["assistant", "Assistant IA du site"]] },
  { id: "application", label: "Application", Icon: Smartphone, ecrans: [["application", "Application"]] },
  // En bas de la barre latérale, au-dessus du compte, comme dans la plupart des outils
  // (école, 23.10.2026).
  { id: "reglages", label: "Réglages", Icon: Settings, enBas: true,
    ecrans: [["reglages", "Réglages"], ["journal", "Journal & comptes"]] },
];
const MENU_DE = Object.fromEntries(TABS.flatMap((t) => t.ecrans.map(([e]) => [e, t])));
// Spectacles : d'abord QUEL spectacle, puis ses onglets (Jen, 02.11.2026 : « Noël au milieu
// du spectacle de juin, ça fait bizarre ») — juin a ses cinq écrans, Noël le sien.
const SPECTACLES = [
  { id: "juin", label: "🎭 Spectacles de juin", accueil: "spectacles" },
  { id: "noel", label: "🎄 Spectacle de Noël", accueil: "spectacles-noel" },
];
const spectacleDe = (ecran) => (ecran === "spectacles-noel" ? "noel" : "juin");
// Les écrans déplacés : « Annonces de l'application » (Messages) est devenu un onglet du
// menu Application ; « Cartes 10 cours » (Élèves) une pastille de Facturation ; « Cours d'essai »
// (Élèves) la section « tdb-essais » du tableau de bord.
const ANCIENS = { annonces: { onglet: "application", vue: "notifications" },
  "cartes-10": { onglet: "facturation", id: "cartes10" }, essais: { onglet: "dashboard", id: "tdb-essais" } };
const moderne = (c) => (c && ANCIENS[c.onglet] ? { ...c, ...ANCIENS[c.onglet] } : c);
// Un lien vers un écran qui n'existe plus ouvre le tableau de bord plutôt qu'une page vide.
const ecranConnu = (id) => (ANCIENS[id] ? ANCIENS[id].onglet : MENU_DE[id] ? id : "dashboard");
// Une alerte peut viser une VUE de l'écran en plus d'une ligne (« Nouvelles attestations à
// envoyer » → Attestations, onglet « Nouvelles à envoyer ») : la cible devient « vue:id ».
const cibleDe = (c) => (c?.vue ? `${c.vue}:${c.id || ""}` : c?.id || null);

export default function Console() {
  const [connecte, setConnecte] = useState(!!jeton());
  const chantiers = useChantiers("console");
  const visible = (t) => !t.chantier || chantiers[t.chantier];
  const ecransVisibles = (t) => (t?.ecrans || []).filter(([, , ch]) => !ch || chantiers[ch]);
  // Le sous-menu d'un écran : pour Spectacles, seulement les onglets du spectacle choisi.
  const ecransDuSousMenu = (e) => {
    const t = MENU_DE[e];
    const tous = ecransVisibles(t);
    return t?.id === "spectacles" ? tous.filter(([x]) => spectacleDe(x) === spectacleDe(e)) : tous;
  };
  // « ?onglet=reglages » (retour de Google après la connexion du Drive, liens d'e-mail) :
  // la console s'ouvre directement sur le bon écran.
  const [onglet, setOnglet] = useState(() => {
    try {
      return ecranConnu(new URLSearchParams(window.location.search).get("onglet") || "");
    }
    catch { return "dashboard"; }
  });
  // Le bouton d'un e-mail à l'école vise aussi une ligne (Jen, 01.11.2026 : « un lien vers
  // le menu en question ») : « ?onglet=profs&vue=decomptes&id=2026-10:prof » se lit comme
  // aller({ onglet, vue, id }) (backend console/mail.lien_console).
  const [cible, setCible] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const id = p.get("onglet") || "";
      const ligne = p.get("id") || "";
      const vue = p.get("vue") || "";
      if (ligne || vue) return cibleDe(moderne({ onglet: id, id: ligne, vue }));
      return ANCIENS[id] ? cibleDe(ANCIENS[id]) : null;
    }
    catch { return null; }
  });
  // Recharger la page garde l'écran ouvert (demandé par l'école le 10.10.2026 : chaque
  // rechargement ramenait au tableau de bord) : l'adresse porte « ?onglet=… », relu
  // ci-dessus au démarrage. Les autres paramètres (« ?avance=1 ») restent.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (onglet === "dashboard") url.searchParams.delete("onglet");
      else url.searchParams.set("onglet", onglet);
      if (url.href !== window.location.href) window.history.replaceState(window.history.state, "", url);
    } catch { /* adresse non modifiable : sans gravité */ }
  }, [onglet]);
  // « &id= » et « &vue= » ne servent qu'à l'ouverture : lus ci-dessus, ils quittent l'adresse
  // une fois la console ouverte (après la connexion s'il le faut : un rechargement de l'écran
  // de connexion garde le lien entier). La cible, elle, reste en mémoire pour l'écran.
  useEffect(() => {
    if (!connecte) return;
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has("id") && !url.searchParams.has("vue")) return;
      url.searchParams.delete("id");
      url.searchParams.delete("vue");
      window.history.replaceState(window.history.state, "", url);
    } catch { /* adresse non modifiable : sans gravité */ }
  }, [connecte]);
  const [notifs, setNotifs] = useState(null);
  const [panneau, setPanneau] = useState(false);
  const [moi, setMoi] = useState(null);
  // Ce que l'écran a ouvert (la feuille d'un stage dans Liste) : son nom dans le fil
  // d'Ariane, et le retour à la liste en haut de page — { titre, retour }.
  const [fil, setFil] = useState(null);

  // Une seule demande à la fois : sur un serveur lent, le rappel des 60 s et chaque
  // changement d'onglet empilaient des dizaines de calculs de « À traiter » (09.10.2026).
  const notifsEnCours = useRef(false);
  const chargerNotifs = useCallback(async () => {
    if (notifsEnCours.current) return;
    notifsEnCours.current = true;
    try { setNotifs(await api("/notifications")); } catch { /* session */ }
    finally { notifsEnCours.current = false; }
  }, []);

  // « À traiter » se recharge à chaque changement d'onglet (une inscription arrivée du
  // site, un message de contact…) et toutes les 60 s tant que l'onglet du navigateur est
  // visible : le badge ne reste plus figé sur l'état du chargement de la page.
  useEffect(() => { if (connecte) chargerNotifs(); }, [connecte, onglet, chargerNotifs]);
  useEffect(() => {
    if (!connecte) return undefined;
    const tic = setInterval(() => {
      if (document.visibilityState === "visible") chargerNotifs();
    }, 60000);
    const surVisibilite = () => {
      if (document.visibilityState === "visible") chargerNotifs();
    };
    document.addEventListener("visibilitychange", surVisibilite);
    return () => {
      clearInterval(tic);
      document.removeEventListener("visibilitychange", surVisibilite);
    };
  }, [connecte, chargerNotifs]);
  useEffect(() => { if (connecte) api("/moi").then(setMoi).catch(() => {}); }, [connecte]);
  useEffect(() => { if (connecte) return garderLaSession(); }, [connecte]);

  useEffect(() => {
    const surSession = () => { setConnecte(false); setMoi(null); };
    window.addEventListener("console-session-expiree", surSession);
    return () => window.removeEventListener("console-session-expiree", surSession);
  }, []);

  // Passer d'un écran à l'autre depuis n'importe où (« Écrire à ce groupe » depuis Liste).
  useEffect(() => {
    const surAller = (e) => {
      setCible(cibleDe(moderne(e.detail)));
      setOnglet(ecranConnu(e.detail?.onglet));
      setPanneau(false);
    };
    window.addEventListener("console-aller", surAller);
    return () => window.removeEventListener("console-aller", surAller);
  }, []);

  useEffect(() => {
    document.body.classList.add("console-admin");
    return () => document.body.classList.remove("console-admin");
  }, []);

  const aller = (c) => {
    setCible(cibleDe(moderne(c)));
    setOnglet(ecranConnu(c?.onglet));
    setPanneau(false);
  };

  const choisirOnglet = (id) => {
    // Un écran déplacé (« cartes-10 » depuis le tableau de bord) : là où il est maintenant.
    if (ANCIENS[id]) { aller({ onglet: id }); return; }
    // Re-cliquer « Liste » (menu ou sous-menu) pendant qu'une feuille est ouverte ramène
    // à la liste, comme « Retour à la liste ».
    if (fil && id === onglet) fil.retour();
    setCible(null); setOnglet(id); setPanneau(false);
  };

  if (!connecte) return <Connexion onOk={() => setConnecte(true)} />;

  const total = notifs?.total || 0;
  const filActif = onglet === "liste" ? fil : null;
  // L'écran annoncé à l'aide : « Réglages → Application », comme les titres de son guide
  // (« Application » seul désignait l'ancien menu).
  const menuActif = MENU_DE[onglet];
  const nomEcran = (menuActif?.ecrans || []).find(([e]) => e === onglet)?.[1] || menuActif?.label || onglet;
  const ecranAide = (menuActif?.ecrans.length || 0) > 1 ? `${menuActif.label} → ${nomEcran}` : nomEcran;
  return (
    <FournisseurConfirmation>
    <div className="min-h-screen flex" style={{ background: "#F4F7F9" }} data-testid="console">
      {/* Barre latérale sombre : elle porte la navigation et donne le contraste */}
      <aside className="hidden lg:flex flex-col w-[248px] shrink-0 sticky top-0 h-screen px-4 py-6" style={{ background: "#0F1B2D" }}>
        {/* Le logo de l'école (22.10.2026), en version claire pour le fond sombre : le noir
            du logo y devient blanc, le turquoise reste. */}
        <div className="px-3 flex items-end gap-3" data-testid="console-logo">
          <img src="/img/1000arts-logo-console.png" alt="1000 ARTS" width="70" height="72"
            className="h-[72px] w-auto shrink-0" />
          <div className="text-[10px] uppercase tracking-[0.2em] pb-2" style={{ color: "#7DE8ED" }}>Console</div>
        </div>
        {[TABS.filter((t) => !t.enBas && visible(t)), TABS.filter((t) => t.enBas)].map((groupe, bas) => (
        <nav key={bas} className={bas ? "pb-3 space-y-1" : "mt-8 space-y-1 flex-1 min-h-0 overflow-y-auto"}
          data-testid={bas ? "console-menu-bas" : "console-menu"}>
          {groupe.map(({ id, label, Icon }) => {
            const actif = MENU_DE[onglet]?.id === id;
            return (
              <button
                key={id}
                data-testid={`console-tab-${id}`}
                onClick={() => choisirOnglet(id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors"
                style={{
                  background: actif ? "var(--primary)" : "transparent",
                  color: actif ? "#04202A" : "rgba(255,255,255,0.68)",
                  fontWeight: actif ? 600 : 400,
                }}
              >
                <Icon size={16} />
                <span className="text-left leading-tight">{label}</span>
                {id === "notifications" && total > 0 && (
                  <span
                    className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: actif ? "#04202A" : "#EF4444", color: "#fff", minWidth: 20 }}
                    data-testid="sidebar-badge-notifications"
                  >
                    {total}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        ))}
        <div className="px-3 pt-4 border-t" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
          <div className="text-[11px] leading-snug" style={{ color: "rgba(255,255,255,0.45)" }}>
            {moi ? <span data-testid="console-compte">Connecté·e : <strong style={{ color: "rgba(255,255,255,0.8)" }}>{moi.nom}</strong></span>
              : "Chantier — la production n'est pas affectée"}
          </div>
          <button
            data-testid="console-logout"
            onClick={() => { localStorage.removeItem("admin_token"); setConnecte(false); }}
            className="mt-3 flex items-center gap-2 text-sm"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            <LogOut size={15} /> Quitter
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b" style={{ borderColor: "var(--border)" }}>
          <div className="px-5 md:px-8 py-3 flex items-center gap-3">
            <img src="/img/9ab2fa8bea.webp" alt="1000 ARTS" width="40" height="40" className="lg:hidden h-10 w-auto shrink-0" />
            <div className="hidden lg:flex items-center gap-2 text-sm text-[var(--muted)] min-w-0" data-testid="console-fil">
              Console <ChevronRight size={14} className="shrink-0" />
              {/* Une feuille ouverte remplace le nom de l'écran : « Liste › Stage d'automne… »
                  dit ce qu'on regarde, « Liste › Listes » ne disait rien. */}
              <span className="text-[var(--text)] font-medium truncate">{MENU_DE[onglet]?.label}{(filActif || (MENU_DE[onglet]?.ecrans.length || 0) > 1) && <> <ChevronRight size={14} className="inline" /> {filActif ? filActif.titre : MENU_DE[onglet].ecrans.find(([e]) => e === onglet)?.[1]}</>}</span>
            </div>
            {/* Sur mobile la barre latérale est masquée : le compte et « Quitter »
                doivent rester atteignables sans elle. */}
            <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="console-logout-mobile"
              onClick={() => { localStorage.removeItem("admin_token"); setConnecte(false); }}
              className="lg:hidden flex items-center gap-1.5 text-xs px-3 py-2 rounded-full"
              style={{ background: "#EEF2F6", color: "var(--muted)" }}
            >
              <LogOut size={14} /> Quitter
            </button>
            <div className="relative">
              <button
                data-testid="console-cloche"
                onClick={() => { setPanneau(!panneau); chargerNotifs(); }}
                className="w-10 h-10 rounded-full flex items-center justify-center relative transition-colors hover:bg-[var(--primary-tint)]"
              >
                <Bell size={19} />
                {total > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 text-[10px] font-bold px-1.5 rounded-full"
                    style={{ background: "#EF4444", color: "#fff", minWidth: 18, lineHeight: "18px" }}
                    data-testid="console-cloche-compteur"
                  >
                    {total > 99 ? "99+" : total}
                  </span>
                )}
              </button>

              {panneau && (
                <div
                  className="absolute right-0 mt-2 w-[360px] max-h-[70vh] overflow-auto bg-white rounded-2xl border shadow-[var(--shadow-lg)]"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="console-panneau-notifications"
                >
                  <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border-soft)" }}>
                    <Eyebrow>À traiter</Eyebrow>
                    <div className="font-display text-lg">{total} action(s)</div>
                  </div>
                  {(notifs?.groupes || []).length === 0 && (
                    <p className="px-4 py-6 text-sm text-[var(--muted)]">Rien à traiter pour le moment.</p>
                  )}
                  {(notifs?.groupes || []).map((g) => (
                    <button
                      key={g.id}
                      data-testid={`panneau-groupe-${g.id}`}
                      onClick={() => { setPanneau(false); choisirOnglet("notifications"); }}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 border-b hover:bg-[var(--primary-tint)] transition-colors"
                      style={{ borderColor: "var(--border-soft)" }}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: g.gravite === "rouge" ? "#DC2626" : g.gravite === "ambre" ? "#F59E0B" : "#3B82F6" }}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium truncate">{g.titre}</span>
                        <span className="block text-xs text-[var(--muted)] truncate">{g.action}</span>
                      </span>
                      <span className="ml-auto text-sm font-semibold">{g.nb}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => choisirOnglet("notifications")}
                    className="w-full px-4 py-3 text-sm font-semibold"
                    style={{ color: "var(--primary)" }}
                    data-testid="panneau-voir-tout"
                  >
                    Voir la liste complète →
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
          {/* Navigation repliée sur mobile */}
          <nav className="lg:hidden px-3 pb-2 flex gap-1 overflow-x-auto">
            {TABS.filter(visible).map(({ id, label }) => (
              <button
                key={id}
                onClick={() => choisirOnglet(id)}
                className="px-3 py-2 text-xs whitespace-nowrap rounded-full"
                style={{
                  background: MENU_DE[onglet]?.id === id ? "var(--primary)" : "#EEF2F6",
                  color: MENU_DE[onglet]?.id === id ? "#04202A" : "var(--muted)",
                  fontWeight: MENU_DE[onglet]?.id === id ? 600 : 400,
                }}
              >
                {label}
                {id === "notifications" && total > 0 && ` · ${total}`}
              </button>
            ))}
          </nav>
        </header>

        <main className="px-5 md:px-8 py-8 max-w-[1400px]">
          {filActif && (
            <div className="mb-4 sans-impression">
              <button onClick={filActif.retour} className="btn-ghost" data-testid="fiche-retour">
                <ArrowLeft size={15} /> Retour à la liste
              </button>
            </div>
          )}
          {/* Le sous-menu reste à l'écran : imprimé, il ouvrait une page presque vide avant
              les feuilles de présence et les listes. Sur téléphone, les onglets se partagent
              la largeur en parts égales et leur nom passe sur deux lignes : les trois écrans
              de Messages tiennent à 390 px sans déborder ni laisser un onglet seul en dessous. */}
          {/* Le choix du spectacle sur sa propre ligne, ses onglets en dessous (Jen, 03.11.2026 : « ce menu est lié
              au spectacle de juin, il devrait se situer en dessous du premier filtre »). */}
          {MENU_DE[onglet]?.id === "spectacles" && (
            <div className="grid grid-cols-2 sm:w-fit gap-2 mb-3 sans-impression" data-testid="console-quel-spectacle">
              {SPECTACLES.map((sp) => {
                const actif = spectacleDe(onglet) === sp.id;
                return (
                  <button key={sp.id} onClick={() => !actif && choisirOnglet(sp.accueil)} aria-pressed={actif}
                    data-testid={`console-spectacle-${sp.id}`}
                    className="px-4 sm:px-5 min-h-[48px] rounded-2xl text-[14px] sm:text-[15px] font-semibold border transition"
                    style={actif ? { background: "var(--text)", color: "#fff", borderColor: "var(--text)" }
                      : { background: "#fff", color: "#334155", borderColor: "var(--border)" }}>
                    {sp.label}
                  </button>
                );
              })}
            </div>
          )}
          {ecransDuSousMenu(onglet).length > 1 && (
            <div className="grid w-full sm:inline-flex sm:w-auto sm:flex-wrap p-1 rounded-2xl gap-1 mb-6 sans-impression"
              style={{ background: "#E2E8F0", gridTemplateColumns: `repeat(${ecransDuSousMenu(onglet).length}, minmax(0, 1fr))` }}
              data-testid="console-sous-menu">
              {ecransDuSousMenu(onglet).map(([e, label]) => (
                <button key={e} onClick={() => choisirOnglet(e)} data-testid={`console-ecran-${e}`}
                  className="px-2 sm:px-4 py-1.5 sm:py-0 min-h-[44px] sm:min-h-[38px] rounded-xl text-[13px] sm:text-sm font-semibold leading-tight"
                  style={onglet === e ? { background: "#fff", color: "var(--text)", boxShadow: "0 1px 3px rgba(15,27,45,0.15)" } : { color: "#475569" }}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {onglet === "dashboard" && <Dashboard nbActions={total} onVoirNotifications={() => choisirOnglet("notifications")} onOuvrirCours={(id) => aller({ onglet: "planning", id })} onVoirEleves={() => choisirOnglet("eleves")} onVoirListe={() => choisirOnglet("liste")} onOuvrir={choisirOnglet} cible={cible} />}
          {onglet === "notifications" && <Notifications key={cible || "notifications"} cible={cible} data={notifs} onRecharger={chargerNotifs} onAller={aller} />}
          {onglet === "liste" && <Liste key={cible || "liste"} cible={cible} onFil={setFil} />}
          {onglet === "eleves" && <Eleves key={cible || "eleves"} cible={cible} />}
          {/* Cours privés : la réservation de la console là où le chantier est ouvert, sinon Calendly. */}
          {onglet === "cours-prives" && (chantiers.cours_prives ? <ReservationsPrivees key={cible || "cours-prives"} cible={cible} /> : <CoursPrives />)}
          {onglet === "messages" && <Messages cible={cible} onChangement={chargerNotifs} aTraiter={notifs} />}
          {onglet === "envois" && <Envois key={cible || "envois"} cible={cible} />}
          {onglet === "emails-envoyes" && <EmailsEnvoyes />}
          {onglet === "attestations" && <Attestations key={cible || "attestations"} cible={cible} onChangement={chargerNotifs} aTraiter={notifs} />}
          {onglet === "planning" && <Planning key={cible || "planning"} cible={cible} />}
          {onglet === "calendrier" && (
            <div className="space-y-4" data-testid="console-calendrier">
              <div>
                <h1 className="font-display text-3xl tracking-tight">Calendrier de l'année</h1>
                <p className="text-sm text-[var(--muted)] mt-1">Vacances et jours fériés (Réglages → Saison et calendrier), portes ouvertes, stages, spectacles, auditions et workshops. Le même calendrier que dans les applications.</p>
              </div>
              <CalendrierAnnee testId="console-annee" large />
            </div>
          )}
          {onglet === "facturation" && <Facturation key={cible || "facturation"} cible={cible} onChangement={chargerNotifs} onAller={aller} />}
          {onglet === "profs" && <Profs key={cible || "profs"} cible={cible} />}
          {onglet === "assistant" && <Assistant aTraiter={notifs} cible={cible} />}
          {onglet === "reglages" && <Reglages />}
          {onglet === "journal" && <Journal />}
          {onglet === "application" && <MenuApplication key={cible || "application"} cible={cible} />}
          {onglet === "spectacles" && <Billetterie onAller={aller} />}
          {onglet === "spectacles-benevoles" && <Billetterie partie="benevoles" key={cible || "benevoles"} cible={cible} onAller={aller} />}
          {onglet === "spectacles-commandes" && <Billetterie partie="commandes" key={cible || "commandes"} cible={cible} onAller={aller} />}
          {onglet === "spectacles-entree" && <Billetterie partie="entree" />}
          {onglet === "spectacles-noel" && <SpectacleNoel />}
          {onglet === "spectacles-reglages" && <Billetterie partie="reglages" />}
        </main>
      </div>
      <VisionneusePdf />
      <AideConsole ecran={ecranAide} />
    </div>
    </FournisseurConfirmation>
  );
}

// Retour de Google (« Continuer avec Google ») : le jeton ou l'erreur arrive dans le fragment
// de l'adresse ; il est lu une fois puis retiré de la barre d'adresse.
const FRAGMENT_GOOGLE = (() => {
  // Seulement sur la page de la console : l'app (/espace) lit ses propres « #erreur=… ».
  if (typeof window === "undefined" || !window.location.pathname.startsWith("/console")) return {};
  const p = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
  const jeton = p.get("jeton");
  const erreur = p.get("erreur");
  if (jeton || erreur) {
    if (jeton) { try { localStorage.setItem("admin_token", jeton); } catch { /* navigation privée */ } }
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  return { erreur };
})();

function Connexion({ onOk }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState(FRAGMENT_GOOGLE.erreur === "google-compte"
    ? "Cette adresse Google n'a pas de compte dans la console. Demande à un administrateur de l'ajouter (Réglages › Journal & comptes)."
    : FRAGMENT_GOOGLE.erreur ? "La connexion avec Google n'a pas abouti. Réessaie." : "");
  const [google, setGoogle] = useState(false);
  useEffect(() => { googleActif().then(setGoogle); }, []);

  const envoyer = async (e) => {
    e.preventDefault();
    setErr("");
    const data = await connexion(form);
    if (!data) return setErr("E-mail ou mot de passe incorrect.");
    onOk();
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#0F1B2D" }}>
      <form onSubmit={envoyer} className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-[var(--shadow-lg)]" data-testid="console-login">
        <img src="/img/9ab2fa8bea.webp" alt="1000 ARTS" width="84" height="84" className="h-[84px] w-auto mb-4 -ml-1" />
        <div className="font-display text-2xl flex items-center gap-2"><Lock size={18} className="text-[var(--muted)]" /> Console 1000 ARTS</div>
        <p className="text-sm text-[var(--muted)] mt-2 mb-6">Accès réservé à l'administration de l'école.</p>
        <label className="field-label">E-mail</label>
        <input data-testid="console-login-email" className="input-field mb-4" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <label className="field-label">Mot de passe</label>
        <input data-testid="console-login-password" className="input-field mb-6" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        {err && <div className="text-sm mb-4" style={{ color: "#B91C1C" }} data-testid="console-login-error">{err}</div>}
        <button className="btn-primary w-full justify-center" data-testid="console-login-submit">Entrer</button>
        {google && (
          <>
            <div className="flex items-center gap-3 my-4 text-xs text-[var(--muted)]"><span className="h-px flex-1 bg-[var(--border)]" />ou<span className="h-px flex-1 bg-[var(--border)]" /></div>
            <a href={urlConnexionGoogle()} className="w-full inline-flex items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold"
              style={{ borderColor: "var(--border)", color: "var(--text)" }} data-testid="console-login-google">
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
              Continuer avec Google
            </a>
          </>
        )}
      </form>
    </div>
  );
}
